import React, { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { dashboardApi, formsApi } from '../../services/api';
import {
    FileText,
    MapPin,
    Users,
    Clock,
    Bell,
    Building2,
    AlertCircle
} from 'lucide-react';

const DashboardPage = () => {
    const { selectedCorporate, corporates } = useOutletContext();
    const [stats, setStats] = useState(null);
    const [recentSubmissions, setRecentSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (selectedCorporate) {
            loadDashboardData();
        } else {
            setStats(null);
            setRecentSubmissions([]);
            setLoading(false);
        }
    }, [selectedCorporate]);

    const loadDashboardData = async () => {
        if (!selectedCorporate?.id) return;
        setLoading(true);
        setError(null);
        try {
            const [statsData, submissionsData] = await Promise.all([
                dashboardApi.getStats(selectedCorporate?.id),
                formsApi.getSubmissions(1, 5, selectedCorporate?.id)
            ]);
            setStats(statsData);
            setRecentSubmissions(submissionsData.items || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-center gap-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
                <div>
                    <h3 className="font-semibold text-red-800">Hata</h3>
                    <p className="text-red-600">{error}</p>
                </div>
            </div>
        );
    }

    const statCards = [
        {
            title: 'Toplam Bildirimler',
            value: stats?.totalFormSubmissions || 0,
            icon: Bell,
            color: 'blue',
            gradient: 'from-blue-500 to-blue-600',
            path: '/admin/submissions'
        },
        {
            title: 'Toplam Lokasyon',
            value: stats?.totalLocations || 0,
            icon: MapPin,
            color: 'green',
            gradient: 'from-green-500 to-green-600',
            path: '/admin/locations'
        },
        {
            title: 'Toplam Kullanıcılar',
            value: stats?.totalUsers || 0,
            icon: Users,
            color: 'purple',
            gradient: 'from-purple-500 to-purple-600',
            path: '/admin/users'
        },
        {
            title: 'Kurumlar',
            value: corporates.length,
            icon: Building2,
            color: 'orange',
            gradient: 'from-orange-500 to-orange-600',
            path: '/admin/corporates'
        }
    ];

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
                <p className="text-gray-500 mt-1">
                    {selectedCorporate ? `${selectedCorporate.name} - Genel Bakış` : 'Tüm Kurumlar - Genel Bakış'}
                </p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {statCards.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                        <Link
                            key={index}
                            to={stat.path}
                            className="bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-all hover:scale-[1.02] cursor-pointer block"
                        >
                            <div className={`bg-gradient-to-r ${stat.gradient} p-4`}>
                                <Icon className="w-8 h-8 text-white" />
                            </div>
                            <div className="p-4">
                                <p className="text-sm text-gray-500">{stat.title}</p>
                                <p className="text-3xl font-bold text-gray-900 mt-1">{stat.value}</p>
                            </div>
                        </Link>
                    );
                })}
            </div>

            {/* Recent Submissions */}
            <div className="bg-white rounded-xl shadow-sm border">
                <div className="px-6 py-4 border-b flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-gray-500" />
                        <h2 className="text-lg font-semibold text-gray-900">Son Bildirimler</h2>
                    </div>
                    <Link
                        to="/admin/submissions"
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                        Tümünü Gör →
                    </Link>
                </div>

                {recentSubmissions.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                        <p>Henüz bildirim bulunmamaktadır.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Form
                                    </th>
                                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Lokasyon
                                    </th>
                                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Kurum
                                    </th>
                                    <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Tarih
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {recentSubmissions.map((submission) => (
                                    <tr key={submission.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <p className="font-medium text-gray-900">{submission.formTitle}</p>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">{submission.locationName}</td>
                                        <td className="px-6 py-4 text-gray-600">{submission.corporateName}</td>
                                        <td className="px-6 py-4 text-gray-500 text-sm">
                                            {formatDate(submission.submittedAt)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DashboardPage;
